import React, { useState, useEffect } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import AiAssistant from './ai/AiAssistant';
import './MainLayout.css';

function MainLayout({ children }) {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    const saved = sessionStorage.getItem('sidebarExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hideCursor, setHideCursor] = useState(false);
  const layoutRef = React.useRef(null);
  const cursorTimeoutRef = React.useRef(null);

  const toggleSidebar = () => {
    setIsSidebarExpanded(prev => {
      const newState = !prev;
      sessionStorage.setItem('sidebarExpanded', JSON.stringify(newState));
      return newState;
    });
  };

  const toggleFullscreen = async () => {
    if (!isFullscreen) {
      // 전체화면 진입 - layout-container 요소를 fullscreen으로
      try {
        const element = layoutRef.current;
        if (element) {
          if (element.requestFullscreen) {
            await element.requestFullscreen();
          } else if (element.webkitRequestFullscreen) {
            await element.webkitRequestFullscreen();
          } else if (element.mozRequestFullScreen) {
            await element.mozRequestFullScreen();
          } else if (element.msRequestFullscreen) {
            await element.msRequestFullscreen();
          }
          setIsFullscreen(true);
          setIsSidebarExpanded(false);
        }
      } catch (err) {
        console.error('전체화면 전환 실패:', err);
      }
    } else {
      // 전체화면 종료
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
        setIsFullscreen(false);
      } catch (err) {
        console.error('전체화면 종료 실패:', err);
      }
    }
  };

  // 브라우저 fullscreen 상태 변경 감지 (ESC 키 등으로 종료 시)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);
      if (!isCurrentlyFullscreen) {
        // 전체화면 종료 시 저장된 사이드바 상태 복원
        const saved = sessionStorage.getItem('sidebarExpanded');
        setIsSidebarExpanded(saved !== null ? JSON.parse(saved) : true);
        setHideCursor(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // 전체화면 모드에서 마우스 자동 숨김 (3초 후)
  useEffect(() => {
    if (!isFullscreen) {
      setHideCursor(false);
      return;
    }

    const handleMouseMove = () => {
      // 커서 표시
      setHideCursor(false);

      // 기존 타이머 제거
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }

      // 3초 후 커서 숨김
      cursorTimeoutRef.current = setTimeout(() => {
        setHideCursor(true);
      }, 3000);
    };

    // 마우스 이동 이벤트 등록
    document.addEventListener('mousemove', handleMouseMove);

    // 초기 타이머 설정
    handleMouseMove();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }
    };
  }, [isFullscreen]);

  // 사이드바 너비는 .layout-container 의 --sidebar-w(접힘 60 / 펼침 240)로 헤더·본문이 함께 따라간다
  return (
    <div className={`layout-container ${isSidebarExpanded && !isFullscreen ? 'sidebar-open' : ''}`} ref={layoutRef}>
      {!isFullscreen && <Header isFullscreen={isFullscreen} toggleFullscreen={toggleFullscreen} />}
      {!isFullscreen && <Sidebar isExpanded={isSidebarExpanded} onToggle={toggleSidebar} />}
      {!isFullscreen && <AiAssistant />}
      <main className={`main-content ${isFullscreen ? 'fullscreen-mode' : ''} ${hideCursor ? 'hide-cursor' : ''}`}>
        {isFullscreen && (
          <div className="fullscreen-exit-btn" onClick={toggleFullscreen} title="전체화면 종료">
            <span className="material-symbols-outlined">fullscreen_exit</span>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

export default MainLayout;
